import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { observer } from "mobx-react-lite";
import type { ApiNotification } from "@hive/sdk";
import { hive } from "../../lib/apiClient";
import { useStore } from "../../stores/rootStore";
import { TopBar } from "./TopBar";
import { SiteFooter } from "./SiteFooter";
import { Toaster } from "./Toaster";
import { AuthModal } from "../auth/AuthModal";
import { InviteBanner } from "../auth/InviteBanner";
import { FriendBridgeModal } from "../social/FriendBridgeModal";
import { RemixPanel } from "../remix/RemixPanel";
import { PurchaseConfirmModal } from "../play/PurchaseConfirmModal";
import { PlayOnMobileModal } from "../mobile/PlayOnMobileModal";
import { PAID_ECONOMY_ENABLED } from "../../config";

const FRIEND_NOTIFICATION_TYPES = new Set<ApiNotification["type"]>([
	"FRIEND_REQUEST",
	"FRIEND_ACCEPT",
	"FRIEND_MATCH",
]);

export const AppLayout = observer(() => {
	const { auth, wallet, friends, dm, notification } = useStore();
	const location = useLocation();
	const navigate = useNavigate();

	// Boot the session once.
	useEffect(() => {
		void auth.bootstrap();
	}, [auth]);

	// 13+ compliance: an age-blocked session has no usable user and 401s every call the chrome
	// below makes. Hand it to the notice page — the one place the birthday can still be corrected.
	useEffect(() => {
		if (auth.initialized && auth.ageRestricted) navigate("/age-restriction", { replace: true });
	}, [auth.initialized, auth.ageRestricted, navigate]);

	// React to sign-in / sign-out: hydrate per-user state, or tear it down.
	useEffect(() => {
		if (!auth.initialized) return;
		if (auth.user) {
			void wallet.load();
			void friends.hydrate(auth.user.id);
			void dm.start(auth.user.id);
			notification.start(auth.user.id);
		} else {
			wallet.reset();
			friends.reset();
			dm.reset();
			notification.stop();
		}
	}, [auth.initialized, auth.user?.id, wallet, friends, dm, notification]);

	useEffect(() => {
		if (!auth.initialized || !auth.user) return;
		void dm.watchFriends(friends.friends);
	}, [auth.initialized, auth.user?.id, friends.friends, dm]);

	// Keep the friends badge fresh when friend activity arrives over the shared
	// realtime stream. The notification store owns the connection lifecycle.
	useEffect(() => {
		if (!auth.initialized || !auth.user) return;
		const offNotification = hive.realtime.on("notification", (item) => {
			if (FRIEND_NOTIFICATION_TYPES.has(item.type)) void friends.refresh();
		});
		const offOpen = hive.realtime.onOpen(() => {
			void friends.refresh();
		});
		return () => {
			offNotification();
			offOpen();
		};
	}, [auth.initialized, auth.user?.id, friends]);

	// The playable player is an immersive full-height view. Direct conversations
	// handle their own responsive overlay: full-screen on mobile, floating on desktop.
	const isImmersive =
		location.pathname.startsWith("/play/") || location.pathname.startsWith("/drama/");
	const isDiscovery = location.pathname === "/" || /^\/all\/tags\/?$/.test(location.pathname) || location.pathname.startsWith("/t/");

	return (
		<div className={`muhaha-atmosphere relative isolate flex min-h-screen min-w-0 flex-col ${isDiscovery ? "discovery-app" : ""}`}>
			<div className="app-layout-body flex min-w-0 flex-1 flex-col">
				{!isImmersive && <TopBar />}
				{!isImmersive && <InviteBanner />}
				<main className={isImmersive || isDiscovery ? "min-h-0 min-w-0 flex-1" : "min-w-0 flex-1 pb-16"}>
					<Outlet />
				</main>
				{!isImmersive && !isDiscovery && <SiteFooter />}
			</div>
			<AuthModal />
			<PlayOnMobileModal />
			<FriendBridgeModal />
			<RemixPanel />
			{PAID_ECONOMY_ENABLED && <PurchaseConfirmModal />}
			<Toaster />
		</div>
	);
});
